import { z } from 'zod';

// ---- Pop-ups (Admin › Pop-ups) -------------------------------------------------------------------

export const POPUP_FREQUENCIES = ['EVERY_VISIT', 'DAILY', 'ONCE'] as const;
export type PopupFrequency = (typeof POPUP_FREQUENCIES)[number];

export const POPUP_FREQUENCY_LABEL: Record<PopupFrequency, string> = {
  EVERY_VISIT: 'Toda vez que abrir a plataforma',
  DAILY: 'Uma vez por dia',
  ONCE: 'Uma única vez (nunca mais)',
};

export const POPUP_TITLE_MAX = 80;

/** An uploaded image (/api/courses/images/…) or an https link. */
const IMAGE_URL = /^(https:\/\/\S+|\/api\/courses\/images\/[A-Za-z0-9_-]{1,40})$/;
/** An https link or a platform path (/aulas, /operar?x=1). "//x" and "/\x" would leave the platform: refused. */
const LINK_URL = /^(https:\/\/\S+|\/(?![/\\])[^\s\\]*)$/;

export const isPopupLink = (v: string) => LINK_URL.test(v.trim());
export const isPopupImage = (v: string) => IMAGE_URL.test(v.trim());

const optionalText = (max: number) =>
  z.preprocess((v) => (typeof v === 'string' ? v.replace(/\u0000/g, '').trim() || null : v), z.string().max(max).nullable());
const optionalDate = z.preprocess((v) => (v === '' ? null : v), z.coerce.date().nullable());

export const popupInputSchema = z
  .object({
    title: optionalText(POPUP_TITLE_MAX).default(null),
    imageUrl: z.string().trim().max(2000).regex(IMAGE_URL, 'Envie uma imagem ou cole um link https.'),
    linkUrl: z.preprocess((v) => (typeof v === 'string' ? v.trim() || null : v), z.string().max(2000).regex(LINK_URL, 'Use um link https:// ou um caminho da plataforma (/aulas).').nullable()).default(null),
    linkNewTab: z.boolean().default(true),
    frequency: z.enum(POPUP_FREQUENCIES).default('DAILY'),
    stopOnClick: z.boolean().default(true),
    startsAt: optionalDate.default(null),
    endsAt: optionalDate.default(null),
    active: z.boolean().default(false),
  })
  .refine((p) => !p.startsAt || !p.endsAt || p.endsAt > p.startsAt, { message: 'O fim precisa ser depois do início.', path: ['endsAt'] });
export type PopupInput = z.input<typeof popupInputSchema>;

/** Partial edit (the list's switch, or the form): the period is checked against the stored one by the API. */
export const popupPatchSchema = z.object({
  title: optionalText(POPUP_TITLE_MAX).optional(),
  imageUrl: z.string().trim().max(2000).regex(IMAGE_URL, 'Envie uma imagem ou cole um link https.').optional(),
  linkUrl: z.preprocess((v) => (typeof v === 'string' ? v.trim() || null : v), z.string().max(2000).regex(LINK_URL, 'Use um link https:// ou um caminho da plataforma (/aulas).').nullable()).optional(),
  linkNewTab: z.boolean().optional(),
  frequency: z.enum(POPUP_FREQUENCIES).optional(),
  stopOnClick: z.boolean().optional(),
  startsAt: optionalDate.optional(),
  endsAt: optionalDate.optional(),
  active: z.boolean().optional(),
});

/** What the user's browser gets: only what it needs to show the pop-up. */
export interface PopupDTO {
  id: string;
  title: string | null;
  imageUrl: string;
  linkUrl: string | null;
  linkNewTab: boolean;
  frequency: PopupFrequency;
  /** Whoever clicks the image never sees it again. */
  stopOnClick: boolean;
}

export interface AdminPopupDTO extends PopupDTO {
  startsAt: string | null;
  endsAt: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  stats: {
    /** People who saw it at least once. */
    reach: number;
    /** Times it was shown, all users. */
    views: number;
    /** People who clicked the image. */
    clickers: number;
  };
}

export type PopupStatus = 'LIVE' | 'SCHEDULED' | 'ENDED' | 'OFF';

/** LIVE = users are seeing it now; SCHEDULED = active but its start is ahead; ENDED = active but past its end. */
export function popupStatus(p: { active: boolean; startsAt: string | Date | null; endsAt: string | Date | null }, now = Date.now()): PopupStatus {
  if (!p.active) return 'OFF';
  if (p.startsAt && new Date(p.startsAt).getTime() > now) return 'SCHEDULED';
  if (p.endsAt && new Date(p.endsAt).getTime() <= now) return 'ENDED';
  return 'LIVE';
}

const brasiliaDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' });

/**
 * Whether this user should get the pop-up now, from what they did with it so far.
 * EVERY_VISIT is limited once more in the browser (once per session).
 */
export function popupDue(
  p: { frequency: PopupFrequency; stopOnClick: boolean },
  seen: { lastSeenAt: Date; clickedAt: Date | null } | null,
  now = new Date(),
): boolean {
  if (!seen) return true;
  if (p.stopOnClick && seen.clickedAt) return false;
  const { frequency } = p;
  if (frequency === 'ONCE') return false;
  if (frequency === 'DAILY') return brasiliaDay.format(seen.lastSeenAt) !== brasiliaDay.format(now);
  return true;
}
